import {
  CPI_INDEX_BY_CURRENCY,
  ISwapLegDetails,
  ISwapTradeMessage,
  SwapInflationDirection,
  TradeInterestType,
} from "@/lib/interfaces and Enums/ENUMSwapTradeMessage";
import { TradeDirection } from "@/lib/interfaces and Enums/ENUMTradeDirection";
import {
  returnReferenceRateFrequency,
  returnReferenceRateOptions,
} from "@/lib/businessRules/referenceRateRule";
import {
  ISwapLegDefaults,
  returnSwapLegDefaults,
} from "@/lib/businessRules/swapConventionsRule";
import {
  TradeMessageSubType,
  TradeMessageType,
} from "@/lib/interfaces and Enums/ENUMBondTradeMessage";
import { IPortfolio } from "@/lib/interfaces and Enums/ENUMPortfolio";
import {
  SwapType,
  isPerLegNominalSwap,
  returnSpreadLegs,
  returnSwapType,
} from "@/lib/businessRules/swapTypeRule";
import { returnSizeFormatted } from "@/lib/globalFormatterHelper";
import {
  ISwapLegTicket,
  ISwapTicket,
  SwapTicketDirection,
} from "@/store/tradeFromScratchStore";
import { returnAllocationIssues, returnWireAllocations } from "./allocationRules";
import {
  ITicketField,
  returnMissingTicketEntries,
  returnRequiredTicketKeys,
  returnTransactionNumberDetail,
} from "./ticketSharedHelper";

// LQC-2674 - pure rules for the swap ticket. No React, no store reads, so all of it is
// unit testable. Mirrors bondTicketHelper / bondOptionTicketHelper, except the leg fields:
// ticketSharedHelper keys off `keyof T` and half of a swap's required fields live one
// level down on a leg, so those keep their own list here.

export type SwapLegKey = "leg1" | "leg2";

export interface ISwapTicketFieldEntry {
  // "nominal", or "leg2.referenceRate" for a leg field.
  key: string;
  label: string;
}

// Rates are entered as percents and sent as decimals: the API bounds couponRate and
// spread to (-1, 1) exclusive, so 2.678521% travels as 0.02678521.
export function returnRateAsWireDecimal(
  percent: number | null | undefined,
): number | undefined {
  if (percent === null || percent === undefined) return undefined;
  return percent / 100;
}

export function isFixedLeg(leg: ISwapLegTicket): boolean {
  return leg.interestType === TradeInterestType.Fixed;
}

// A fixed leg must carry NO reference rate and a floating leg MUST carry one -
// ValidateSwapReferenceRate rejects either mistake outright. Shared with the swaption
// ticket, whose legs follow the same rules.
// requireCoupon: a new swap is priced by the dealer so has no coupon yet (LQC-1899), but a
// swaption's fixed coupon is its strike.
export function returnRequiredLegFields(
  leg: ISwapLegTicket,
  requireCoupon = false,
): { field: keyof ISwapLegTicket; label: string }[] {
  const fields: { field: keyof ISwapLegTicket; label: string }[] = [
    { field: "currency", label: "Currency" },
    { field: "couponFrequency", label: "Frequency" },
    { field: "dayCount", label: "Day Count" },
    { field: "businessDay", label: "Business Day" },
    { field: "calendar", label: "Calendar" },
  ];
  if (!isFixedLeg(leg)) {
    return [...fields, { field: "referenceRate", label: "Reference Rate" }];
  }
  if (requireCoupon) return [...fields, { field: "couponRate", label: "Coupon" }];
  return fields;
}

// 0% is a real coupon, so couponRate is checked for presence rather than truthiness.
function isLegFieldMissing(
  leg: ISwapLegTicket,
  field: keyof ISwapLegTicket,
): boolean {
  if (field === "couponRate") return typeof leg.couponRate !== "number";
  return !leg[field];
}

export function returnMissingLegEntries(
  leg: ISwapLegTicket,
  key: SwapLegKey,
  legLabel: string,
  requireCoupon = false,
): ISwapTicketFieldEntry[] {
  return returnRequiredLegFields(leg, requireCoupon)
    .filter(({ field }) => isLegFieldMissing(leg, field))
    .map(({ field, label }) => ({
      key: `${key}.${field}`,
      label: `${legLabel} ${label}`,
    }));
}

export function returnRequiredLegKeys(
  leg: ISwapLegTicket,
  key: SwapLegKey,
  requireCoupon = false,
): string[] {
  return returnRequiredLegFields(leg, requireCoupon).map(({ field }) => `${key}.${field}`);
}

// The trade-level required fields. isDate rather than a truthiness check: a native date
// input can yield a five digit year, which is truthy and unsendable (LQC-2677).
export const SWAP_TICKET_REQUIRED_TRADE_FIELDS: ITicketField<ISwapTicket>[] = [
  { key: "portfolioCode", label: "Portfolio" },
  { key: "nominal", label: "Nominal", isNumeric: true },
  { key: "direction", label: "Direction" },
  { key: "startDate", label: "Start Date", isDate: true },
  { key: "maturityDate", label: "Maturity", isDate: true },
  { key: "settlementDate", label: "Settlement Date", isDate: true },
  { key: "tradeDate", label: "Trade Date", isDate: true },
];

const NOMINAL_LEG_2_FIELD: ITicketField<ISwapTicket> = {
  key: "nominal2",
  label: "Nominal Leg 2",
  isNumeric: true,
};

export function returnSwapTicketType(ticket: ISwapTicket): SwapType {
  return returnSwapType(ticket.leg1, ticket.leg2);
}

export function isPerLegNominalTicket(ticket: ISwapTicket): boolean {
  return isPerLegNominalSwap(returnSwapTicketType(ticket));
}

// Leg 2 nominal is only asked for when the legs can differ (XCS/XBS).
function returnSwapTicketTradeFields(
  ticket: ISwapTicket,
): ITicketField<ISwapTicket>[] {
  if (!isPerLegNominalTicket(ticket)) return SWAP_TICKET_REQUIRED_TRADE_FIELDS;
  return [...SWAP_TICKET_REQUIRED_TRADE_FIELDS, NOMINAL_LEG_2_FIELD];
}

export function returnMissingSwapTicketEntries(
  ticket: ISwapTicket,
): ISwapTicketFieldEntry[] {
  return [
    ...returnMissingTicketEntries(ticket, returnSwapTicketTradeFields(ticket)).map(
      ({ key, label }) => ({ key: String(key), label }),
    ),
    ...returnMissingLegEntries(ticket.leg1, "leg1", "Leg 1"),
    ...returnMissingLegEntries(ticket.leg2, "leg2", "Leg 2"),
    ...returnCpiIssues(ticket),
    ...returnAllocationIssues(ticket.nominal, ticket.additionalAllocations),
  ];
}

// Spec F17: changing couponFrequency re-defaults a floating leg's resetFrequency, never
// the reverse. A fixed leg has no reset frequency to follow.
export function returnCouponFrequencyChanges(
  leg: ISwapLegTicket,
  couponFrequency: string,
): Partial<ISwapLegTicket> {
  if (isFixedLeg(leg)) return { couponFrequency };
  return { couponFrequency, resetFrequency: couponFrequency };
}

// The rate a floating leg falls back to when its list no longer holds the current one:
// the first index for a ZCS (Q10), else the currency convention, else a term rate that
// matches the coupon frequency.
function returnFallbackReferenceRate(
  leg: ISwapLegTicket,
  options: string[],
  isZcs: boolean,
): string {
  if (isZcs) return options[0] ?? "";
  const convention = returnSwapLegDefaults(leg.currency, leg.interestType).referenceRate;
  if (options.includes(convention)) return convention;
  const termMatch = options.find(
    (rate) => returnReferenceRateFrequency(rate) === leg.couponFrequency,
  );
  return termMatch ?? options[0] ?? "";
}

// LQC-2977 (spec F17) - keeps each floating leg's rate inside its currency, coupon
// frequency and ZCS list, so the dropdown never shows one rate while another is sent.
export function returnLegsWithValidReferenceRates<L extends ISwapLegTicket>(legs: {
  leg1: L;
  leg2: L;
}): { leg1: L; leg2: L } {
  const isZcs = returnSwapType(legs.leg1, legs.leg2) === SwapType.ZCS;
  const restamp = (leg: L): L => {
    if (leg.interestType !== TradeInterestType.Floating) return leg;
    const options = returnReferenceRateOptions(leg.currency, isZcs, leg.couponFrequency);
    if (options.includes(leg.referenceRate)) return leg;
    return { ...leg, referenceRate: returnFallbackReferenceRate(leg, options, isZcs) };
  };
  return { leg1: restamp(legs.leg1), leg2: restamp(legs.leg2) };
}

// A coupon frequency edit on a Swap or Swaption leg: resyncs the reset frequency, then the
// rates. A Zero frequency makes it a ZCS, which swaps rates for inflation indices.
export function returnLegFrequencyChanges(
  ticket: { leg1: ISwapLegTicket; leg2: ISwapLegTicket },
  legKey: SwapLegKey,
  couponFrequency: string,
): { leg1: ISwapLegTicket; leg2: ISwapLegTicket } {
  const legs = { leg1: ticket.leg1, leg2: ticket.leg2 };
  legs[legKey] = {
    ...legs[legKey],
    ...returnCouponFrequencyChanges(legs[legKey], couponFrequency),
  };
  return returnLegsWithValidReferenceRates(legs);
}

// What the CPI rules read: a Swap, or a Swaption's underlying (LQC-2959).
export type ICpiTicket = Pick<ISwapTicket, "leg1" | "leg2" | "cpi">;

// LQC-2957 - the inflation index a ZCS's CPI comes from: its floating leg's reference rate
// ("EUR-HICP"), the code GET /ReferenceData/CPI matches. "" when not a ZCS.
export function returnZcsInflationIndex(ticket: ICpiTicket): string {
  if (!isZcsTicket(ticket)) return "";
  const floating = [ticket.leg1, ticket.leg2].find(
    (leg) => leg.interestType === TradeInterestType.Floating,
  );
  return floating?.referenceRate ?? "";
}

// A seasoned swap (started before today) takes the CPI from before its start; otherwise current.
export function returnCpiStartDate(startDate: string, today: string): string | undefined {
  return startDate && startDate < today ? startDate : undefined;
}

// Fills CPI from a lookup unless the user typed their own (cpiAutoKey ""). A new lookup
// (index or start date changed) replaces a value that was itself filled in, and clears it
// when the new lookup has none: a current CPI on a seasoned swap would be wrong.
export function returnCpiAutoFillChanges(
  ticket: { cpi: number | null; cpiAutoKey: string },
  key: string,
  cpi: number | null,
): { cpi?: number | null; cpiAutoKey?: string } {
  const isUserValue = ticket.cpi !== null && ticket.cpiAutoKey === "";
  if (isUserValue) return {};
  if (cpi === null) {
    return ticket.cpiAutoKey && ticket.cpiAutoKey !== key ? { cpi: null, cpiAutoKey: "" } : {};
  }
  if (ticket.cpiAutoKey === key && ticket.cpi === cpi) return {};
  return { cpi, cpiAutoKey: key };
}

// A CPI commit only makes it the user's own when the value changed: the input commits on
// every blur, so clicking in and out of a filled CPI must not mark it "entered".
export function returnCpiCommitChanges(
  ticket: { cpi: number | null },
  cpi: number | null,
): { cpi?: number | null; cpiAutoKey?: string } {
  return cpi === ticket.cpi ? {} : { cpi, cpiAutoKey: "" };
}

// LQC-2957 - the index the API accepts for this ZCS, from Leg 2's currency ("" if none).
export function returnZcsCpiIndex(ticket: ICpiTicket): string {
  return CPI_INDEX_BY_CURRENCY[ticket.leg2.currency] ?? "";
}

export function isZcsTicket(ticket: ICpiTicket): boolean {
  return returnSwapType(ticket.leg1, ticket.leg2) === SwapType.ZCS;
}

// ValidateZeroNullOrGreaterThan(1): a CPI of 0.5 is rejected, so say so before sending.
export function returnCpiIssues(ticket: ICpiTicket): ISwapTicketFieldEntry[] {
  if (!isZcsTicket(ticket) || typeof ticket.cpi !== "number") return [];
  if (ticket.cpi === 0 || ticket.cpi > 1) return [];
  return [{ key: "cpi", label: "CPI (must be above 1)" }];
}

// Only a ZCS carries CPI; the index is always sent with it, the value only once set.
export function returnZcsCpiDetails(ticket: ICpiTicket): {
  cpi?: number;
  cpiIndex?: string;
} {
  if (!isZcsTicket(ticket)) return {};
  const cpiIndex = returnZcsCpiIndex(ticket);
  return {
    ...(cpiIndex ? { cpiIndex } : {}),
    ...(typeof ticket.cpi === "number" ? { cpi: ticket.cpi } : {}),
  };
}

// "" means it follows the start date (spec F9); the user's own value wins once set.
export function returnEffectiveFirstCouponFrom(ticket: {
  firstCouponFrom: string;
  startDate: string;
}): string {
  return ticket.firstCouponFrom || ticket.startDate;
}

// LQC-2991 - Buy / Sell Inflation mark the swap as ZCS; they are never sent.
export const SWAP_TICKET_DIRECTIONS: SwapTicketDirection[] = [
  TradeDirection.Pay,
  TradeDirection.Receive,
  SwapInflationDirection.BuyInflation,
  SwapInflationDirection.SellInflation,
];

export function isInflationDirection(direction: string): boolean {
  return (
    direction === SwapInflationDirection.BuyInflation ||
    direction === SwapInflationDirection.SellInflation
  );
}

// Buying inflation pays fixed and receives the CPI leg.
export function returnSwapWireDirection(direction: SwapTicketDirection): string {
  if (direction === SwapInflationDirection.BuyInflation) return TradeDirection.Pay;
  if (direction === SwapInflationDirection.SellInflation) return TradeDirection.Receive;
  return direction;
}

const ZERO_FREQUENCY = "Zero";

// The currency's conventions with a Zero coupon, which is what makes the API class it
// ZCS. The floating leg takes the currency's first inflation index (LEG_CREATION_PLAN Q10).
export function returnZcsLegDefaults(
  currency: string,
  interestType: TradeInterestType,
): ISwapLegDefaults {
  const defaults = returnSwapLegDefaults(currency, interestType);
  if (interestType === TradeInterestType.Fixed) {
    return { ...defaults, couponFrequency: ZERO_FREQUENCY };
  }
  return {
    ...defaults,
    couponFrequency: ZERO_FREQUENCY,
    resetFrequency: ZERO_FREQUENCY,
    referenceRate: returnReferenceRateOptions(currency, true)[0] ?? "",
  };
}

export function returnLegDefaultsForDirection(
  direction: string,
  currency: string,
  interestType: TradeInterestType,
): ISwapLegDefaults {
  return isInflationDirection(direction)
    ? returnZcsLegDefaults(currency, interestType)
    : returnSwapLegDefaults(currency, interestType);
}

function returnRestampedLeg(
  leg: ISwapLegTicket,
  direction: string,
): ISwapLegTicket {
  return {
    ...leg,
    ...returnLegDefaultsForDirection(direction, leg.currency, leg.interestType),
  };
}

// Crossing between vanilla and inflation re-stamps both legs; any other change is just
// the direction. Leg edits never change the direction (LEG_CREATION_PLAN Q9).
export function returnSwapDirectionChanges(
  ticket: ISwapTicket,
  direction: SwapTicketDirection,
): Partial<ISwapTicket> {
  const isInflation = isInflationDirection(direction);
  if (isInflation === isInflationDirection(ticket.direction)) return { direction };
  // Crossing re-stamps the legs, and the template is now on the wrong side of the filter.
  if (!isInflation) {
    return {
      direction,
      templateName: "",
      leg1: returnRestampedLeg(ticket.leg1, direction),
      leg2: returnRestampedLeg(ticket.leg2, direction),
    };
  }
  // A ZCS is fixed against the CPI leg.
  return {
    direction,
    templateName: "",
    leg1: returnRestampedLeg(
      { ...ticket.leg1, interestType: TradeInterestType.Fixed, spread: null },
      direction,
    ),
    leg2: returnRestampedLeg(
      { ...ticket.leg2, interestType: TradeInterestType.Floating, couponRate: null },
      direction,
    ),
  };
}

// Every key the ticket can mark required, leg fields included. Leg keys depend on the
// leg's type, so this is a function of the ticket rather than a constant.
export function returnRequiredSwapTicketKeys(ticket: ISwapTicket): Set<string> {
  return new Set([
    ...returnRequiredTicketKeys(returnSwapTicketTradeFields(ticket)),
    ...returnRequiredLegKeys(ticket.leg1, "leg1"),
    ...returnRequiredLegKeys(ticket.leg2, "leg2"),
  ]);
}

// LQC-2973 - nominal2 is in every payload: captured for XCS/XBS, else a copy of nominal.
export function returnAllocationNominal2(ticket: ISwapTicket): number {
  if (isPerLegNominalTicket(ticket)) return ticket.nominal2 as number;
  return ticket.nominal as number;
}

// Each allocation's Leg 2 nominal: a copy, or its pro-rata share of Leg 2 for XCS/XBS
// (LEG_CREATION_PLAN Q23). Multiply first so a whole allocation stays exact.
export function returnNominal2ForAllocation(
  ticket: ISwapTicket,
): (allocationNominal: number) => number {
  if (!isPerLegNominalTicket(ticket)) return (allocationNominal) => allocationNominal;
  const nominal = ticket.nominal as number;
  const nominal2 = returnAllocationNominal2(ticket);
  return (allocationNominal) => (allocationNominal * nominal2) / nominal;
}

export function returnMissingSwapTicketFields(ticket: ISwapTicket): string[] {
  return returnMissingSwapTicketEntries(ticket).map((entry) => entry.label);
}

export function isSwapTicketComplete(ticket: ISwapTicket): boolean {
  return returnMissingSwapTicketEntries(ticket).length === 0;
}

// The API only demands a reset frequency when the two legs are in different currencies
// (ValidateSwapResetFrequency), but a floating leg is meaningless without one.
export function needsResetFrequency(ticket: ISwapTicket): boolean {
  if (ticket.leg1.currency !== ticket.leg2.currency) return true;
  return !isFixedLeg(ticket.leg1) || !isFixedLeg(ticket.leg2);
}

// ValidateSwapResetFrequency needs BOTH legs' reset frequency once the currencies
// differ, fixed leg included; the API itself falls back to the coupon frequency, so we
// send that. Shared with the swaption builder, which the API checks the same way.
export function returnWireResetFrequency(
  leg: ISwapLegTicket,
  isCrossCurrency: boolean,
): string {
  if (leg.resetFrequency) return leg.resetFrequency;
  return isCrossCurrency ? leg.couponFrequency : "";
}

// Maturity must be on or after settlement - ValidateMaturityDateWithSettlementDate.
export function isMaturityAfterSettlement(ticket: ISwapTicket): boolean {
  if (!ticket.maturityDate || !ticket.settlementDate) return true;
  return ticket.maturityDate >= ticket.settlementDate;
}

// Names the field, so the message can sit on the row that is wrong.
export function returnSwapDateIssue(
  ticket: ISwapTicket,
): { key: "maturityDate"; message: string } | null {
  if (isMaturityAfterSettlement(ticket)) return null;
  return {
    key: "maturityDate",
    message: `Must be on or after settlement (${ticket.settlementDate})`,
  };
}

export function returnSentSwapTicketSummary(ticket: ISwapTicket): string {
  const nominal =
    typeof ticket.nominal === "number" && ticket.nominal > 0
      ? returnSizeFormatted(ticket.nominal)
      : "";
  const fixed = isFixedLeg(ticket.leg1) ? ticket.leg1 : ticket.leg2;
  const rate =
    typeof fixed.couponRate === "number" ? `@ ${fixed.couponRate}%` : "";
  return [
    ticket.direction,
    nominal,
    ticket.leg1.currency,
    returnSwapTicketType(ticket),
    rate,
  ]
    .filter(Boolean)
    .join(" ");
}

// Optional members are omitted rather than sent empty: a reference rate on a fixed leg
// is rejected, and a 0 spread is not the same as no spread.
// allowSpread: a spread left on a leg that can no longer take one is dropped (LQC-2967).
function returnLegDetails(
  leg: ISwapLegTicket,
  allowSpread: boolean,
  isCrossCurrency: boolean,
): ISwapLegDetails {
  const details: ISwapLegDetails = {
    interestType: leg.interestType,
    couponFrequency: leg.couponFrequency,
    currency: leg.currency,
    currencyPayment: leg.currency,
    dayCount: leg.dayCount,
    businessDay: leg.businessDay,
    calendar: leg.calendar,
  };
  if (typeof leg.couponRate === "number") {
    details.couponRate = returnRateAsWireDecimal(leg.couponRate);
  }
  const resetFrequency = returnWireResetFrequency(leg, isCrossCurrency);
  if (resetFrequency) details.resetFrequency = resetFrequency;
  if (isFixedLeg(leg)) return details;

  details.referenceRate = leg.referenceRate;
  if (allowSpread && typeof leg.spread === "number") {
    details.spread = returnRateAsWireDecimal(leg.spread);
  }
  return details;
}

export interface IBuildSwapTradeMessageParams {
  ticket: ISwapTicket;
  portfolio: IPortfolio | undefined;
  traderName: string;
  // Used only when the portfolio row carries no customerCode.
  fallbackEntity?: string;
}

export function buildSwapTradeMessage({
  ticket,
  portfolio,
  traderName,
  fallbackEntity = "",
}: IBuildSwapTradeMessageParams): ISwapTradeMessage | undefined {
  if (!isSwapTicketComplete(ticket)) return undefined;
  if (!portfolio) return undefined;
  if (!isMaturityAfterSettlement(ticket)) return undefined;
  const nominal = ticket.nominal as number;
  const spreadLegs = returnSpreadLegs(ticket.leg1, ticket.leg2);
  const isCrossCurrency = ticket.leg1.currency !== ticket.leg2.currency;

  return {
    messageType: TradeMessageType.Trade,
    subType: TradeMessageSubType.Swap,
    clientDetails: {
      originatingEntity: portfolio.customerCode || fallbackEntity,
      lei: portfolio.lei,
      traderName,
    },
    // A swap needs no security lookup, and a non-empty list would make the API demand
    // a UTI / ExternalID / ExternalKey it will never get.
    security: {},
    securityDetails: {
      startDate: ticket.startDate,
      maturityDate: ticket.maturityDate,
      // Tracks the start date, which is what 2078 of 2160 real swaps do.
      firstCouponFrom: returnEffectiveFirstCouponFrom(ticket),
      cleared: ticket.cleared,
      ...returnZcsCpiDetails(ticket),
      securityDetailsLeg1: returnLegDetails(ticket.leg1, spreadLegs.leg1, isCrossCurrency),
      securityDetailsLeg2: returnLegDetails(ticket.leg2, spreadLegs.leg2, isCrossCurrency),
    },
    tradeDetails: {
      tradeDate: ticket.tradeDate,
      direction: returnSwapWireDirection(ticket.direction),
      nominal,
      settlementDate: ticket.settlementDate,
      openClose: ticket.openClose,
      // Only meaningful on a cleared swap, and the API takes "" as absent.
      clearingHouse: ticket.cleared ? ticket.clearingHouse : "",
      ...returnTransactionNumberDetail(ticket.transactionNumber),
    },
    allocations: returnWireAllocations(
      portfolio.portfolioCode,
      nominal,
      ticket.additionalAllocations,
      returnNominal2ForAllocation(ticket),
    ),
  };
}
